import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { Box, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import scrollLeft from "../../assets/icons/scroll-arrow-left.svg";
import scrollRight from "../../assets/icons/scroll-arrow-right.svg";
import useStyles from "./ScrollableImageList.styles";
import Image from "../Image";

const MAX_ZOOM_LEVEL = 3;
// Scale applied on top of the "fill the stage" size once zoomed (level 0
// shows the image contained within its own smaller stage, no scale/pan).
// 1 = 100% (big, whole image still visible), 2 = double, 3 = 300% -
// overflowing the stage, pannable.
const SCALE_STEPS: Record<number, number> = { 1: 1, 2: 2, 3: 3 };
// A pointer move shorter than this is treated as a click (cycles zoom),
// anything longer is treated as a pan drag.
const DRAG_THRESHOLD_PX = 4;

const CLIPS_OVERFLOW = /(auto|scroll|hidden|clip)/;

// This app nests ScrollableImageList inside all sorts of scrollable side
// panels/drawers (overflow: auto/hidden), which sit well inside the browser
// window. The nearest such ancestor - not the raw window - is what actually
// clips the zoomed overlay, so walk up from the element to find the tightest
// vertical bound any ancestor's own overflow imposes.
const getVisibleVerticalBounds = (el: HTMLElement): { top: number; bottom: number } => {
  let top = 0;
  let bottom = window.innerHeight;
  let node = el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (CLIPS_OVERFLOW.test(style.overflowY) || CLIPS_OVERFLOW.test(style.overflow)) {
      const rect = node.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }
    node = node.parentElement;
  }
  return { top, bottom };
};

interface ScrollableImageListProps {
  images: string[];
  imageWidth?: number;
  imageHeight?: number;
  showArrows?: boolean;
  onImageClick?: (idx: number) => void;
  placeholder?: React.ReactNode;
}

const ScrollableImageList: React.FC<ScrollableImageListProps> = ({
  images,
  imageWidth = 92,
  imageHeight = 122,
  showArrows = true,
  onImageClick,
  placeholder,
}) => {
  const [zoomLevel, setZoomLevel] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const classes = useStyles({ imageWidth, imageHeight, zoomLevel });
  const scrollRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const zoomedImgRef = useRef<HTMLImageElement>(null);
  const zoomFrameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ startX: 0, startY: 0, startPanX: 0, startPanY: 0, moved: false });

  // zoomOverlay is deliberately scoped to a small band on the page (not the
  // full viewport - see the comment on zoomOverlay in the styles), and this
  // component is used inside all sorts of scrollable side panels/drawers
  // (overflow: auto/hidden) that are themselves smaller than the browser
  // window. A flat vh-based height cap ignores both of these: centering a
  // tall box on a band near the edge of whichever scroll container it's
  // actually clipped by can push the box's top edge - and the close button
  // anchored to its corner - out of view (off past the real clipping
  // ancestor, not just the raw window). Bounding the height by the actual
  // room available above/below the band's own vertical center, *within the
  // nearest clipping ancestor* (see getVisibleVerticalBounds), keeps the box
  // symmetric and guarantees it (and the close button) stay visible,
  // regardless of the image's aspect ratio or which panel it's embedded in.
  //
  // This has to be measured DOM geometry, not something derivable from props/
  // state, so it's a layout effect (not a useMemo reading overlayRef.current -
  // refs aren't reactive, and reading one during render can only ever see the
  // value from *before* this render committed) that re-measures after every
  // commit where open/zoomLevel change, once the ref is guaranteed attached.
  const [maxBoxPx, setMaxBoxPx] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const overlayEl = overlayRef.current;
    if (!open || !overlayEl) {
      setMaxBoxPx(null);
      return;
    }
    const overlayRect = overlayEl.getBoundingClientRect();
    const overlayMidY = overlayRect.top + overlayRect.height / 2;
    const { top: visibleTop, bottom: visibleBottom } = getVisibleVerticalBounds(overlayEl);
    const verticalRoomPx =
      2 * Math.max(0, Math.min(overlayMidY - visibleTop, visibleBottom - overlayMidY));
    setMaxBoxPx({
      width: overlayRect.width * (zoomLevel > 0 ? 0.92 : 0.55),
      height: Math.min(window.innerHeight * (zoomLevel > 0 ? 0.88 : 0.48), verticalRoomPx),
    });
  }, [open, zoomLevel]);

  // The wrapper's max-bound size (see maxBoxPx) is only a safe upper bound -
  // at levels 0/1 the image usually doesn't fill it on both axes (a portrait
  // photo in a wider frame, say), which left the close button (anchored to
  // the wrapper's corner) sitting with a visible gap from the actual image
  // edge. Once we know the image's real natural size, size the wrapper to the
  // image's *actual* rendered footprint at the current zoom level instead
  // (still capped at the same max bounds, so it can never exceed the dialog).
  const fitBoxPx = useMemo(() => {
    if (!naturalSize || !maxBoxPx) return null;
    const { width: maxWidthPx, height: maxHeightPx } = maxBoxPx;
    if (!maxWidthPx || !maxHeightPx) return null;
    const containScale = Math.min(maxWidthPx / naturalSize.width, maxHeightPx / naturalSize.height);
    const scale = SCALE_STEPS[zoomLevel] ?? 1;
    return {
      width: Math.min(naturalSize.width * containScale * scale, maxWidthPx),
      height: Math.min(naturalSize.height * containScale * scale, maxHeightPx),
    };
  }, [naturalSize, maxBoxPx, zoomLevel]);

  const updateScrollButtons = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  useEffect(() => {
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener("scroll", updateScrollButtons);
      updateScrollButtons();
      return () => ref.removeEventListener("scroll", updateScrollButtons);
    }
  }, [images, imageWidth, imageHeight, updateScrollButtons]);

  const scroll = useCallback(
    (direction: "left" | "right") => {
      if (scrollRef.current) {
        const scrollAmount = imageWidth * 2;
        scrollRef.current.scrollBy({
          left: direction === "left" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });
      }
    },
    [imageWidth]
  );

  const handleImageClick = useCallback(
    (idx: number) => {
      setSelectedIdx(idx);
      setOpen(true);
      setZoomLevel(0);
      setPan({ x: 0, y: 0 });
      setNaturalSize(null);
      onImageClick?.(idx);
    },
    [onImageClick]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setZoomLevel(0);
    setPan({ x: 0, y: 0 });
  }, []);
  const handlePrev = useCallback(() => {
    setSelectedIdx((prev) => Math.max(prev - 1, 0));
    setZoomLevel(0);
    setPan({ x: 0, y: 0 });
    setNaturalSize(null);
  }, []);
  const handleNext = useCallback(() => {
    setSelectedIdx((prev) => Math.min(prev + 1, images.length - 1));
    setZoomLevel(0);
    setPan({ x: 0, y: 0 });
    setNaturalSize(null);
  }, [images.length]);
  // Cycles: 0 (default) -> 1 (big) -> 2 (double first zoom) -> 3 (300%) -> 0 (close zoom)
  const handleImageZoomClick = useCallback(() => {
    setZoomLevel((prev) => (prev + 1) % (MAX_ZOOM_LEVEL + 1));
    setPan({ x: 0, y: 0 });
  }, []);

  const clampPan = useCallback((x: number, y: number) => {
    const frame = zoomFrameRef.current;
    const img = zoomedImgRef.current;
    if (!frame || !img) return { x: 0, y: 0 };
    const frameRect = frame.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const maxX = Math.max(0, (imgRect.width - frameRect.width) / 2);
    const maxY = Math.max(0, (imgRect.height - frameRect.height) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      if (zoomLevel === 0) return; // nothing to pan yet, let it fall through to the click-to-zoom handler
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
        moved: false,
      };
      setIsDragging(true);
    },
    [zoomLevel, pan]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      if (zoomLevel === 0 || !isDragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (!dragRef.current.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        dragRef.current.moved = true;
      }
      if (dragRef.current.moved) {
        setPan(
          clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy)
        );
      }
    },
    [zoomLevel, isDragging, clampPan]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      if (zoomLevel === 0) {
        handleImageZoomClick();
        return;
      }
      setIsDragging(false);
      if (!dragRef.current.moved) {
        // it was a tap/click, not a drag - cycle the zoom level
        handleImageZoomClick();
      }
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    },
    [zoomLevel, handleImageZoomClick]
  );

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handlePrev, handleNext, handleClose]);

  // Arrow Button Renderer
  const renderArrowButton = (direction: "left" | "right") => {
    const isLeft = direction === "left";
    const isDisabled = open
      ? isLeft
        ? selectedIdx === 0
        : selectedIdx === images.length - 1
      : isLeft
      ? !canScrollLeft
      : !canScrollRight;
    return (
      <IconButton
        className={`${classes.arrowButton} ${
          isLeft ? classes.arrowButtonLeft : classes.arrowButtonRight
        }`}
        onClick={() => {
          if (open) {
            isLeft ? handlePrev() : handleNext();
          } else {
            scroll(direction);
          }
        }}
        aria-label={`Scroll ${direction}`}
        disabled={isDisabled}
      >
        <img
          src={isLeft ? scrollLeft : scrollRight}
          alt={`scroll ${direction}`}
          style={{ width: 32, height: 32 }}
        />
      </IconButton>
    );
  };

  if (!images || images.length === 0) {
    return (
      <Box className={classes.root}>
        {placeholder ?? <Box className={classes.placeholder}>No Image</Box>}
      </Box>
    );
  }

  return (
    <Box className={classes.root} style={{ position: "relative" }}>
      {/* Left Arrow */}
      {showArrows && images.length > 1 && (open || canScrollLeft) && renderArrowButton("left")}

      {/* Image List */}
      <div
        ref={scrollRef}
        className={classes.scrollContainer}
        tabIndex={0}
        aria-label="Scrollable image list"
        style={{ pointerEvents: open ? "none" : "auto" }}
      >
        {images.map((src, idx) => (
          <Image 
            isProtected
            key={idx}
            src={src}
            alt={`Image ${idx + 1}`}
            width={imageWidth}
            height={imageHeight}
            className={classes.image}
            onClick={() => handleImageClick(idx)}
            style={{ borderRadius: 8 }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleImageClick(idx);
              }
            }}
            onError={(e) => {
              e.currentTarget.src = "";
              e.currentTarget.alt = "Image not available";
            }}
          />
        ))}
      </div>

      {/* Right Arrow */}
      {showArrows && images.length > 1 && (open || canScrollRight) && renderArrowButton("right")}

      {/* Zoomed Overlay */}
      {open && (
        <div
          ref={overlayRef}
          className={classes.zoomOverlay}
          role="presentation"
          onClick={handleClose}
        >
          <div
            className={classes.zoomedImageWrapper}
            role="presentation"
            style={
              fitBoxPx
                ? { width: fitBoxPx.width, height: fitBoxPx.height }
                : maxBoxPx
                ? { width: maxBoxPx.width, height: maxBoxPx.height }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sibling of zoomStage (not nested in it), pinned to this
                wrapper's own corner - the box that actually matches the
                zoomed image's visible bounds - so it's always exactly at the
                top-right corner of the zoomed view and never affected by the
                image's transform or the stage's overflow:hidden. */}
            <IconButton
              className={classes.closeZoomButton}
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              aria-label="Close zoomed image"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <div ref={zoomFrameRef} className={classes.zoomStage}>
              <Image
                ref={zoomedImgRef}
                isProtected
                src={images[selectedIdx]}
                alt={`Zoomed Image ${selectedIdx + 1}`}
                className={classes.zoomedImage}
                style={{
                  transform:
                    zoomLevel > 0
                      ? `translate(${pan.x}px, ${pan.y}px) scale(${SCALE_STEPS[zoomLevel]})`
                      : undefined,
                  cursor:
                    zoomLevel >= 2
                      ? isDragging
                        ? "grabbing"
                        : "grab"
                      : "zoom-in",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalWidth && img.naturalHeight) {
                    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={zoomLevel > 0}
                aria-label={
                  zoomLevel >= MAX_ZOOM_LEVEL
                    ? "Click to reset zoom"
                    : zoomLevel >= 2
                    ? "Click to zoom in further, or drag to pan"
                    : "Click to zoom in"
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleImageZoomClick();
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default React.memo(ScrollableImageList);